import * as React from "react";
import { Upload, RefreshCw, ZoomIn, X, Target, Save } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { getMaskWithMarker, changeMask, updateSurfaceMarker } from "../../services/api";
import { useSpaceStore } from "../../stores/spaceStore";
import { useToast } from "../ui/Toast";
import { base64ToImageUrl } from "../../lib/utils";
import { TextureOffsetEditor } from "./TextureOffsetEditor";
import type { Surface } from "../../types";

interface MaskEditorProps {
  surface: Surface;
}

export function MaskEditor({ surface }: MaskEditorProps) {
  const { spaceId } = useSpaceStore();
  const [maskUrl, setMaskUrl] = React.useState<string | null>(null);
  const [markerImageUrl, setMarkerImageUrl] = React.useState<string | null>(null);
  const [markerPosition, setMarkerPosition] = React.useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = React.useState({ width: 0, height: 0 });
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isSavingMarker, setIsSavingMarker] = React.useState(false);
  const [showFullMask, setShowFullMask] = React.useState(false);
  const [showFullMarker, setShowFullMarker] = React.useState(false);
  const [markerDirty, setMarkerDirty] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const markerImageRef = React.useRef<HTMLImageElement>(null);

  const { addToast } = useToast();

  // Load mask and marker when surface changes
  React.useEffect(() => {
    loadMaskAndMarker();
  }, [surface.type, surface.index]);

  const loadMaskAndMarker = async () => {
    setIsLoading(true);
    try {
      const response = await getMaskWithMarker(surface.type, surface.index, spaceId || undefined);

      if (response.status === "SUCCESS") {
        // Set mask image
        if (response.mask) {
          setMaskUrl(base64ToImageUrl(response.mask));
        } else {
          setMaskUrl(null);
        }

        // Set marker image
        if (response.marker_image) {
          setMarkerImageUrl(base64ToImageUrl(response.marker_image));
        } else {
          setMarkerImageUrl(null);
        }

        // Set marker position
        if (response.marker_x !== undefined && response.marker_y !== undefined) {
          setMarkerPosition({ x: response.marker_x, y: response.marker_y });
        }
      } else {
        throw new Error(response.message || "Failed to load mask");
      }
    } catch (error) {
      console.error("Failed to load mask:", error);
      addToast("error", "Failed to load mask");
      setMaskUrl(null);
      setMarkerImageUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      addToast("error", "Please select an image file");
      return;
    }

    setIsUploading(true);
    try {
      const base64 = await fileToBase64(file);
      // Don't recompute geometry when editing mask - user can click "Recompute Geometry" if needed
      const response = await changeMask(surface.type, surface.index, base64, false, spaceId || undefined);

      if (response.status === "SUCCESS") {
        addToast("success", "Mask updated successfully");
        await loadMaskAndMarker();
      } else {
        throw new Error(response.message || "Failed to update mask");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update mask";
      addToast("error", message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Get target dimensions from the current mask/marker image
        // This ensures the uploaded mask matches the original image size
        const targetWidth = imageDimensions.width || img.naturalWidth;
        const targetHeight = imageDimensions.height || img.naturalHeight;

        // Draw image onto canvas, resizing if needed
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }
        // Fill with black background first (for transparency handling)
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Draw the image scaled to target dimensions
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        // Export as PNG without alpha issues
        const dataUrl = canvas.toDataURL("image/png");
        const base64 = dataUrl.split(",")[1];

        // Log if resizing occurred
        if (img.naturalWidth !== targetWidth || img.naturalHeight !== targetHeight) {
          console.log(`Resized mask from ${img.naturalWidth}x${img.naturalHeight} to ${targetWidth}x${targetHeight}`);
        }

        resolve(base64);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleMarkerImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;

    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);

    setMarkerPosition({ x, y });
    setMarkerDirty(true);
  };

  const handleMarkerImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const handleSaveMarker = async () => {
    setIsSavingMarker(true);
    try {
      const response = await updateSurfaceMarker(
        surface.type,
        surface.index,
        markerPosition.x,
        markerPosition.y,
        spaceId || undefined
      );

      if (response.status === "SUCCESS") {
        addToast("success", "Marker position saved");
        setMarkerDirty(false);
        // Reload to get updated marker image
        await loadMaskAndMarker();
      } else {
        throw new Error(response.message || "Failed to save marker");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save marker";
      addToast("error", message);
    } finally {
      setIsSavingMarker(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mask Card */}
        <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Mask: {surface.label}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMaskAndMarker}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
              <Button
                size="sm"
                onClick={handleUploadClick}
                loading={isUploading}
              >
                <Upload className="h-4 w-4 mr-1" />
                Replace
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="relative bg-muted rounded-lg overflow-hidden border">
            {isLoading ? (
              <div className="aspect-video flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : maskUrl ? (
              <button
                onClick={() => setShowFullMask(true)}
                className="w-full group relative"
              >
                <img
                  src={maskUrl}
                  alt={`Mask for ${surface.label}`}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ) : (
              <div className="aspect-video flex items-center justify-center text-muted-foreground">
                No mask available
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            White areas represent the surface. Click "Replace" to upload a new mask.
          </p>
        </CardContent>
      </Card>

      {/* Marker Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Marker Position
            </span>
            <Button
              size="sm"
              onClick={handleSaveMarker}
              loading={isSavingMarker}
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Coordinates */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">X:</span>
              <Input
                type="number"
                value={markerPosition.x}
                onChange={(e) => setMarkerPosition((p) => ({ ...p, x: Number(e.target.value) }))}
                className="w-20"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Y:</span>
              <Input
                type="number"
                value={markerPosition.y}
                onChange={(e) => setMarkerPosition((p) => ({ ...p, y: Number(e.target.value) }))}
                className="w-20"
              />
            </div>
          </div>

          <div className="relative bg-muted rounded-lg overflow-hidden border">
            {isLoading ? (
              <div className="aspect-video flex items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : markerImageUrl ? (
              <div className="relative">
                <img
                  ref={markerImageRef}
                  src={markerImageUrl}
                  alt={`Marker for ${surface.label}`}
                  className="w-full h-auto cursor-crosshair"
                  onClick={handleMarkerImageClick}
                  onLoad={handleMarkerImageLoad}
                />
                {/* Show crosshair at current marker position */}
                {imageDimensions.width > 0 && markerDirty && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${(markerPosition.x / imageDimensions.width) * 100}%`,
                      top: `${(markerPosition.y / imageDimensions.height) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {/* Crosshair */}
                    <div className="relative w-10 h-10">
                      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-red-500 -translate-y-1/2" />
                      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-red-500 -translate-x-1/2" />
                      <div className="absolute top-1/2 left-1/2 w-4 h-4 border-2 border-red-500 rounded-full -translate-x-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video flex items-center justify-center text-muted-foreground">
                No marker image available
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Click on the image to set a new marker position, then click "Save".
            {markerDirty && <span className="text-warning ml-1">(Unsaved changes)</span>}
          </p>
        </CardContent>
      </Card>
      </div>

      {/* Texture Offset Editor */}
      <TextureOffsetEditor surface={surface} />

      {/* Full Mask Dialog */}
      {showFullMask && maskUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowFullMask(false)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={maskUrl}
              alt={`Mask for ${surface.label}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowFullMask(false)}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Full Marker Image Dialog */}
      {showFullMarker && markerImageUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowFullMarker(false)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={markerImageUrl}
              alt={`Marker for ${surface.label}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowFullMarker(false)}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
